/**
 * FrameworkSidebar — shows role competencies + behavior bullets for selected level,
 * with an expandable section for the next job level.
 */
import { useState } from 'react';
import { C, BEHAVIORS, FINN_VALUES, LEADERSHIP_VALUES, getCompetencies, getCompetencyDescription, getBehaviorGroup, getLeadershipValueGroup, getTrack, LEADERSHIP_READINESS, ALL_LEVELS } from '../../data/managerReviewData';
import type { Employee } from '../../types/managerReview';

const renderBold = (text: string) => {
  if (!text || !text.includes('**')) return text;
  const parts = text.split('**');
  return parts.map((p, i) => i % 2 === 1 ? <strong key={i}>{p}</strong> : p);
};

const parseSentences = (text: string | null | undefined): string[] => {
  if (!text) return [];
  const cleaned = text.replace(/\n\*\*\n/g, '\n\n').replace(/\n\*\*$/, '').replace(/^\*\*\n/, '');
  if (cleaned.includes('\n\n')) return cleaned.split('\n\n').map(s => s.trim()).filter(Boolean);
  if (cleaned.includes('\n')) return cleaned.split('\n').map(s => s.trim()).filter(Boolean);
  return cleaned.split(/\. (?=[A-Z])/).map((s, i, a) => i < a.length - 1 ? s + '.' : s).filter(Boolean);
};

interface FrameworkSidebarProps {
  employee: Employee;
  level: string;
}

export default function FrameworkSidebar({ employee, level }: FrameworkSidebarProps) {
  // null = collapsed, 'E5'/'M5' (or next level) = expanded for that track
  const [nextTrack, setNextTrack] = useState<string | null>(null);
  const [prevLevel, setPrevLevel] = useState(level);

  // Reset when level changes — avoids showing stale next-level content (setState during render, not in effect)
  if (level !== prevLevel) {
    setPrevLevel(level);
    setNextTrack(null);
  }

  const isIC4 = level === 'IC4';
  const isM = getTrack(level) === 'M';
  const comps = getCompetencies(employee.role, level);
  const behaviorData = BEHAVIORS[getBehaviorGroup(level)] || BEHAVIORS['IC3'];

  // Determine possible next levels
  const levelIdx = ALL_LEVELS.indexOf(level as typeof ALL_LEVELS[number]);
  const hasNextLevel = isIC4 || (levelIdx >= 0 && levelIdx < ALL_LEVELS.length - 1);
  const defaultNextLevel = isIC4 ? 'E5' : (hasNextLevel ? ALL_LEVELS[levelIdx + 1] : null);

  // Which next level is currently shown
  const shownNextLevel = nextTrack;
  const nextIsM = shownNextLevel ? getTrack(shownNextLevel) === 'M' : false;
  const nextComps = shownNextLevel ? (getCompetencies(employee.role, shownNextLevel) || []) : [];
  const nextBehaviorData = shownNextLevel ? (BEHAVIORS[getBehaviorGroup(shownNextLevel)] || BEHAVIORS['IC3']) : null;

  const toggleNext = (track: string) => setNextTrack(prev => prev === track ? null : track);

  const sidebarLabel = (txt: string) => (
    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#999', marginBottom: 10 }}>{txt}</div>
  );

  return (
    <div style={{ padding: '20px 20px 40px' }}>
      {/* Header: role + level badge + next-level toggle (upper right) */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 20 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 2 }}>{employee.role}</div>
          <div style={{ fontSize: 11, color: C.muted }}>{level}</div>
        </div>

        {hasNextLevel && (
          <div style={{ display: 'flex', gap: 5, flexShrink: 0, marginTop: 2 }}>
            {isIC4 ? (
              <>
                {(['E5', 'M5'] as const).map(track => {
                  const active = nextTrack === track;
                  return (
                    <button
                      key={track}
                      onClick={() => toggleNext(track)}
                      style={{
                        padding: '3px 9px', borderRadius: 6, cursor: 'pointer', fontFamily: 'Inter',
                        fontSize: 11, fontWeight: 700,
                        border: `1.5px solid ${active ? C.blue : '#d0d0d0'}`,
                        background: active ? C.blue : '#fff',
                        color: active ? '#fff' : '#666',
                        transition: 'all 0.15s',
                      }}
                    >
                      {track === 'E5' ? 'Expert (E5)' : 'Leadership (M5)'}
                    </button>
                  );
                })}
              </>
            ) : defaultNextLevel ? (
              <button
                onClick={() => toggleNext(defaultNextLevel)}
                style={{
                  padding: '3px 9px', borderRadius: 6, cursor: 'pointer', fontFamily: 'Inter',
                  fontSize: 11, fontWeight: 700,
                  border: `1.5px solid ${nextTrack ? C.blue : '#d0d0d0'}`,
                  background: nextTrack ? C.blue : '#fff',
                  color: nextTrack ? '#fff' : '#666',
                  transition: 'all 0.15s',
                }}
              >
                {defaultNextLevel} {nextTrack ? '▲' : '▼'}
              </button>
            ) : null}
          </div>
        )}
      </div>

      {sidebarLabel('Competencies')}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {comps === null ? (
          <div style={{
            padding: '10px 14px',
            background: 'rgba(232,41,74,0.06)', border: '1px solid rgba(232,41,74,0.35)',
            borderRadius: 7, fontSize: 12, color: '#b91c2e', lineHeight: 1.5,
          }}>
            {/* Name, never an email address: this bundle is publicly downloadable. */}
            ⚠️ Role <strong>"{employee.role}"</strong> is not in the competency database.
            Please contact <strong>Anna Kaiser</strong> (People team).
          </div>
        ) : comps.map(c => {
          const sentences = parseSentences(getCompetencyDescription(c, level, employee.role));
          return (
            <div key={c} style={{ background: '#fff', border: '1px solid #e4e4e4', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: sentences.length ? 7 : 0 }}>{c}</div>
              {sentences.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3, listStyleType: 'disc' }}>
                  {sentences.map((s, i) => (
                    <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>{renderBold(s)}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
        {isM && Object.entries(LEADERSHIP_VALUES[getLeadershipValueGroup(level)] || {}).map(([name, desc]) => {
          const sentences = parseSentences(desc);
          return (
            <div key={name} style={{ background: '#fff', border: '1px solid #e4e4e4', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: sentences.length ? 7 : 0 }}>{name}</div>
              {sentences.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3, listStyleType: 'disc' }}>
                  {sentences.map((s, i) => (
                    <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>{renderBold(s)}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {employee.hasLeadershipReadiness && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#999' }}>Leadership Readiness</div>
            <a
              href="https://finn.atlassian.net/wiki/spaces/HR/pages/4576968732/Leadership+Readiness+Framework"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 10, color: C.blue, fontWeight: 600, textDecoration: 'none', flexShrink: 0 }}
            >Framework ↗</a>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.entries(LEADERSHIP_READINESS).map(([name, desc]) => {
              const sentences = parseSentences(desc);
              return (
                <div key={name} style={{ background: '#fff', border: '1px solid #e4e4e4', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontWeight: 700, fontSize: 12, marginBottom: sentences.length ? 7 : 0 }}>{name}</div>
                  {sentences.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3, listStyleType: 'disc' }}>
                      {sentences.map((s, i) => (
                        <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>{renderBold(s)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sidebarLabel('Behaviors')}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {FINN_VALUES.map(v => {
          const bullets = behaviorData[v.name] || [];
          return (
            <div key={v.name} style={{ background: '#fff', border: '1px solid #e4e4e4', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: bullets.length ? 7 : 0 }}>{v.name}</div>
              {bullets.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3, listStyleType: 'disc' }}>
                  {bullets.map((b, i) => (
                    <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>{renderBold(b)}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Next level section ── */}
      {shownNextLevel && nextBehaviorData && (
        <div style={{ marginTop: 24, borderTop: '1px solid #ebebeb', paddingTop: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.blue, marginBottom: 14 }}>
            {shownNextLevel} — Next level framework
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Next level competencies */}
            <div>
              {sidebarLabel('Competencies')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {nextComps.map(c => {
                  const sentences = parseSentences(getCompetencyDescription(c, shownNextLevel, employee.role));
                  return (
                    <div key={c} style={{ background: '#f5f9ff', border: '1px solid #d8e6fa', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: sentences.length ? 7 : 0, color: C.blue }}>{c}</div>
                      {sentences.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3, listStyleType: 'disc' }}>
                          {sentences.map((s, i) => (
                            <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>{renderBold(s)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
                {nextIsM && Object.entries(LEADERSHIP_VALUES[getLeadershipValueGroup(shownNextLevel)] || {}).map(([name, desc]) => {
                  const sentences = parseSentences(desc);
                  return (
                    <div key={name} style={{ background: '#f5f9ff', border: '1px solid #d8e6fa', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: sentences.length ? 7 : 0, color: C.blue }}>{name}</div>
                      {sentences.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3, listStyleType: 'disc' }}>
                          {sentences.map((s, i) => (
                            <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>{renderBold(s)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Next level behaviors */}
            <div>
              {sidebarLabel('Behaviors')}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {FINN_VALUES.map(v => {
                  const bullets = nextBehaviorData[v.name] || [];
                  return (
                    <div key={v.name} style={{ background: '#f5f9ff', border: '1px solid #d8e6fa', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: bullets.length ? 7 : 0, color: C.blue }}>{v.name}</div>
                      {bullets.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3, listStyleType: 'disc' }}>
                          {bullets.map((b, i) => (
                            <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>{renderBold(b)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
